import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SignInSplash } from '@/components/auth/SignInSplash';
import { UserOnboardingWizard } from '@/components/onboarding/UserOnboardingWizard';
import { AcceptInvitationDialog } from '@/components/orgs/AcceptInvitationDialog';
import { SetBillingCountryDialog } from '@/components/orgs/SetBillingCountryDialog';
import { AcknowledgeUrgentNoticeDialog } from '@/components/notices/AcknowledgeUrgentNoticeDialog';
import { useUser } from '@/contexts/UserContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useAgreement } from '@/contexts/AgreementContext';
import { useSystemNotices } from '@/contexts/SystemNoticesContext';
import { getGetMeQueryKey, getGetMeOrganizationsQueryKey } from '@/api/auth/self/self';
import { useGetOrganization, getGetOrganizationQueryKey } from '@/api/auth/organizations/organizations';
import { OrganizationMembershipRole } from '@/api/auth/missionImpactAuthAPI.schemas';
import { logger } from '@/services/logger';

interface OnboardingContextType {
  /** Whether the user is fully ready to access the app */
  isReady: boolean;
  /** Whether the user needs to complete onboarding */
  needsOnboarding: boolean;
  /** Whether the user needs to accept agreements */
  needsAgreementAcceptance: boolean;
  /** Whether the user has pending invites to handle */
  needsInviteAction: boolean;
  /** Whether the user needs to set billing country for their org */
  needsBillingCountry: boolean;
  /** Whether the user needs to acknowledge urgent system notices */
  needsUrgentNoticeAcknowledgement: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

/**
 * Provider component that orchestrates the onboarding flow
 * Gates access to the app until all requirements are met:
 * 1. User must complete onboarding (or have active orgs if already onboarded)
 * 2. User must accept any pending agreements
 * 3. User must handle any pending invites (blocking)
 * 4. Admin users must set billing country for orgs that don't have one
 * 5. User must acknowledge any urgent system notices
 *
 * Priority order: Onboarding → Agreements → Invites → Billing Country → Urgent Notices
 */
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { meProfile, isLoading: isUserLoading } = useUser();
  const { activeOrgs, pendingInvites, selectedOrganization, selectedOrganizationId, isLoading: isOrgsLoading, acceptInvitation, declineInvitation, isProcessingInvitation } = useOrgs();
  const { pendingAgreements, isChecked: isAgreementChecked, isLoading: isAgreementLoading, refetch: refetchAgreements } = useAgreement();
  const { urgentNotices, isLoading: isNoticesLoading, acknowledgeNotice } = useSystemNotices();
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Compute requirements
  const isOnboarded = meProfile?.onboarded === true;
  const hasActiveOrgs = activeOrgs.length > 0;
  const hasPendingAgreements = pendingAgreements.length > 0;

  // Check if user is admin of selected org
  const isAdminOfSelectedOrg = selectedOrganization?.role === OrganizationMembershipRole.Administrator;

  // Fetch full org details to check billing country (only if admin)
  const { data: orgDetailsResponse, isLoading: isLoadingOrgDetails } = useGetOrganization(
    selectedOrganizationId ?? '',
    {
      query: {
        enabled: !!selectedOrganizationId && isAdminOfSelectedOrg && isOnboarded && hasActiveOrgs && !hasPendingAgreements && pendingInvites.length === 0,
      },
    }
  );
  const orgDetails = orgDetailsResponse?.data ?? null;

  // User needs wizard if: not onboarded, OR onboarded but no orgs, OR onboarded with orgs but pending agreements
  const needsOnboarding = useMemo(() => {
    if (meProfile === null) return false; // Profile not loaded yet
    return !isOnboarded || !hasActiveOrgs || hasPendingAgreements;
  }, [meProfile, isOnboarded, hasActiveOrgs, hasPendingAgreements]);

  // For the context value - more specific flags
  const needsAgreementAcceptance = isOnboarded && hasActiveOrgs && hasPendingAgreements;

  const needsInviteAction = useMemo(() => {
    // Only show invite dialog for fully onboarded users with active orgs who have no pending agreements
    // (Invites during onboarding are handled in the wizard)
    if (!isOnboarded || !hasActiveOrgs || hasPendingAgreements) return false;
    return pendingInvites.length > 0;
  }, [isOnboarded, hasActiveOrgs, hasPendingAgreements, pendingInvites]);

  // Check if billing country needs to be set
  const needsBillingCountry = useMemo(() => {
    // Only check after all other flows are complete
    if (needsOnboarding || needsAgreementAcceptance || needsInviteAction) return false;
    // Only applies to admins
    if (!isAdminOfSelectedOrg) return false;
    // Wait for org details to load
    if (!orgDetails) return false;
    // Check if billing country is missing
    return !orgDetails.billingCountry;
  }, [needsOnboarding, needsAgreementAcceptance, needsInviteAction, isAdminOfSelectedOrg, orgDetails]);

  // Check if urgent notices need to be acknowledged
  const needsUrgentNoticeAcknowledgement = useMemo(() => {
    // Only check after all other flows are complete (comes last)
    if (needsOnboarding || needsAgreementAcceptance || needsInviteAction || needsBillingCountry) return false;
    // Check if there are any urgent unacknowledged notices
    return urgentNotices.length > 0;
  }, [needsOnboarding, needsAgreementAcceptance, needsInviteAction, needsBillingCountry, urgentNotices]);

  const isReady = useMemo(() => {
    return !needsOnboarding && !needsAgreementAcceptance && !needsInviteAction && !needsBillingCountry && !needsUrgentNoticeAcknowledgement;
  }, [needsOnboarding, needsAgreementAcceptance, needsInviteAction, needsBillingCountry, needsUrgentNoticeAcknowledgement]);

  // Check if we're still loading initial data
  const isLoading = isUserLoading || isOrgsLoading || isAgreementLoading || !isAgreementChecked || isNoticesLoading ||
    (isAdminOfSelectedOrg && isOnboarded && hasActiveOrgs && !hasPendingAgreements && pendingInvites.length === 0 && isLoadingOrgDetails);

  /**
   * Handle wizard completion - refresh relevant queries and context state
   */
  const handleWizardComplete = useCallback(async () => {
    // Invalidate queries to refresh state
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getGetMeOrganizationsQueryKey() });
    // Refetch agreements to update the AgreementContext's local state
    await refetchAgreements();
    logger.info('Onboarding wizard completed', {
      context: 'OnboardingContext.handleWizardComplete',
    });
    setIsWizardOpen(false);
  }, [queryClient, refetchAgreements]);

  /**
   * Handle accepting current invitation
   */
  const handleAcceptInvitation = useCallback(async () => {
    const currentInvite = pendingInvites[0];
    await acceptInvitation(currentInvite.orgId);
  }, [pendingInvites, acceptInvitation]);

  /**
   * Handle declining current invitation
   */
  const handleDeclineInvitation = useCallback(async () => {
    const currentInvite = pendingInvites[0];
    await declineInvitation(currentInvite.orgId);
  }, [pendingInvites, declineInvitation]);

  /**
   * Handle billing country set completion
   */
  const handleBillingCountryComplete = useCallback(() => {
    // Invalidate org queries to refresh billing country state
    if (selectedOrganizationId) {
      void queryClient.invalidateQueries({ queryKey: getGetOrganizationQueryKey(selectedOrganizationId) });
    }
    void queryClient.invalidateQueries({ queryKey: getGetMeOrganizationsQueryKey() });
    logger.info('Billing country set', {
      context: 'OnboardingContext.handleBillingCountryComplete',
      orgId: selectedOrganizationId,
    });
  }, [queryClient, selectedOrganizationId]);

  /**
   * Handle urgent notice acknowledgement
   */
  const handleAcknowledgeUrgentNotice = useCallback(async (noticeId: string) => {
    await acknowledgeNotice({ noticeId });
    logger.info('Urgent notice acknowledged', {
      context: 'OnboardingContext.handleAcknowledgeUrgentNotice',
      noticeId,
    });
  }, [acknowledgeNotice]);

  // Show loading state while fetching initial data (but not when wizard is already open)
  if (isLoading && !isWizardOpen) {
    return <SignInSplash />;
  }

  // Show onboarding wizard if user needs onboarding, org setup, or EULA acceptance
  if (needsOnboarding || isWizardOpen) {
    if (!isWizardOpen) {
      setIsWizardOpen(true);
    }
    return (
      <UserOnboardingWizard
        onComplete={() => void handleWizardComplete()}
        isOnboarded={isOnboarded}
        hasActiveOrgs={hasActiveOrgs}
      />
    );
  }

  // Priority 3: Show invite dialog (blocking)
  if (needsInviteAction && pendingInvites[0]) {
    return (
      <AcceptInvitationDialog
        invitation={pendingInvites[0]}
        onAccept={() => void handleAcceptInvitation()}
        onDecline={() => void handleDeclineInvitation()}
        isProcessing={isProcessingInvitation}
      />
    );
  }

  // Priority 4: Show billing country dialog for admins of orgs without billing country
  if (needsBillingCountry && selectedOrganization && selectedOrganizationId) {
    return (
      <SetBillingCountryDialog
        orgId={selectedOrganizationId}
        orgName={selectedOrganization.orgName}
        onComplete={handleBillingCountryComplete}
      />
    );
  }

  // Priority 5: Show urgent notice acknowledgement (comes last, show one at a time)
  if (needsUrgentNoticeAcknowledgement && urgentNotices[0]) {
    return (
      <AcknowledgeUrgentNoticeDialog
        notice={urgentNotices[0]}
        onAcknowledge={handleAcknowledgeUrgentNotice}
      />
    );
  }

  // All requirements met - render children
  return (
    <OnboardingContext.Provider
      value={{
        isReady,
        needsOnboarding,
        needsAgreementAcceptance,
        needsInviteAction,
        needsBillingCountry,
        needsUrgentNoticeAcknowledgement,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}
